import { Grid } from '@material-ui/core'
import TableCell from '@material-ui/core/TableCell'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { PolicyTimeslot_DaysEnum, PolicyRule_VehicleTypesEnum, PolicyTimeslot } from 'src/api/gen/api'
import { IPolicyRuleAbsoluteFleetSize } from 'src/api/types/policy'
import { IBaseTableProps } from 'src/commons/policies/rules'
import { TRulesValidation } from 'src/commons/policies/RulesValidation'
import { isEndTimeBeforeStartTime } from 'src/commons/policies/utils'
import NumericInput from 'src/components/Input/NumericInput'
import { SimpleTable } from 'src/components/Table'
import SETTINGS from 'src/settings'

import { FieldTitle } from '../../commons'
import { GeographiesField, RuleNameField, VehicleTypesField } from '../fields'

import TimeSlotForm from './TimeSlot'

const MAXIMUM_DEVICES = 1000000

type Row = [
  TRulesValidation,
  string, // name
  string, // ruleId
  number, // minimum
  number, // maximum
  PolicyRule_VehicleTypesEnum[], // vehicleTypes
  string[], // geographies
  string[] // buffered_geographies
]

function shouldDisplayError(minimum: number, maximum: number) {
  return minimum > maximum || minimum < 0 || maximum < 0 || (!maximum && minimum > 0) || maximum > MAXIMUM_DEVICES
}

function FleetSizeRule(props: IBaseTableProps<IPolicyRuleAbsoluteFleetSize>) {
  const { t } = useTranslation()

  const HEADERS = [
    t('rule.name'),
    t('rule.count.minimum'),
    t('rule.count.maximum'),
    t('rule.count.vehicle_types'),
    t('rule.geographies'),
  ]

  const handleChangeRule =
    (ruleId: string, key: keyof IPolicyRuleAbsoluteFleetSize) =>
    (val: string | string[] | number | PolicyRule_VehicleTypesEnum[] | null): void => {
      const newRules: IPolicyRuleAbsoluteFleetSize[] = props.rules.map((rule) => {
        if (rule.rule_id !== ruleId) {
          return rule
        }
        return { ...rule, [key]: val }
      })
      props.handleChange(newRules)
    }

  function handleChangeTimeslot(timeslot: PolicyTimeslot, index: number, key: keyof PolicyTimeslot) {
    return (val: string | PolicyTimeslot_DaysEnum[] | null): void => {
      const timeslotsToUpdate: PolicyTimeslot[] = props.rules[0].timeslots
      timeslotsToUpdate[index] = { ...timeslot, [key]: val }
      const newRules: IPolicyRuleAbsoluteFleetSize[] = [{ ...props.rules[0], ['timeslots']: timeslotsToUpdate }]
      props.handleChange(newRules)
    }
  }

  const items = props.rules.map(
    (rule: IPolicyRuleAbsoluteFleetSize, idx): Row => [
      props.validation[idx],
      rule.name,
      rule.rule_id,
      rule.minimum,
      rule.maximum,
      rule.vehicle_types,
      rule.geographies,
      rule.buffered_geographies,
    ]
  )

  return (
    <>
      <SimpleTable
        headerRow={HEADERS.map((title: string) => (
          <TableCell key={`key-${title}`}>
            <FieldTitle title={title} />
          </TableCell>
        ))}
        items={items}
        rowRenderer={([ruleValidation, name, ruleId, minimum, maximum, vehicleTypes, geographies]: Row) => (
          <>
            <TableCell
              style={{ height: '100px', verticalAlign: 'bottom' }}
              align="justify"
              id="absolute_fleet_size_fields_name"
            >
              <RuleNameField
                value={name}
                handleChange={handleChangeRule(ruleId, 'name')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('name')}
                helperText={ruleValidation.getFieldErrorMsg('name')}
              />
            </TableCell>

            <TableCell
              style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '200px' }}
              align="justify"
              id="absolute_fleet_size_fields_minimum"
            >
              <NumericInput
                value={minimum}
                onChange={handleChangeRule(ruleId, 'minimum')}
                disabled={props.disabled}
                error={shouldDisplayError(minimum, maximum)}
                helperText={
                  (shouldDisplayError(minimum, maximum) &&
                    (t('pricingPolicy.ruleBadValue', { maximum: MAXIMUM_DEVICES }) as string)) ||
                  ''
                }
              />
            </TableCell>
            <TableCell
              style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '200px' }}
              align="justify"
              id="absolute_fleet_size_fields_maximum"
            >
              <NumericInput
                value={maximum}
                onChange={handleChangeRule(ruleId, 'maximum')}
                disabled={props.disabled}
                error={shouldDisplayError(minimum, maximum)}
                helperText={
                  (shouldDisplayError(minimum, maximum) &&
                    (t('pricingPolicy.ruleBadValue', { maximum: MAXIMUM_DEVICES }) as string)) ||
                  ''
                }
              />
            </TableCell>

            <TableCell
              style={{ height: '100px', verticalAlign: 'bottom', maxWidth: '280px' }}
              align="justify"
              id="absolute_fleet_size_fields_vehicle_types"
            >
              <VehicleTypesField
                value={vehicleTypes}
                handleChange={handleChangeRule(ruleId, 'vehicle_types')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('vehicle_types')}
                helperText={ruleValidation.getFieldErrorMsg('vehicle_types')}
              />
            </TableCell>

            <TableCell
              style={{ verticalAlign: 'bottom', height: '100px', maxWidth: '280px' }}
              align="justify"
              id="absolute_fleet_size_fields_geographies"
            >
              <GeographiesField
                value={geographies}
                allGeographies={props.allGeographies}
                handleChange={handleChangeRule(ruleId, 'geographies')}
                disabled={props.disabled}
                error={!ruleValidation.isFieldValid('geographies')}
                helperText={ruleValidation.getFieldErrorMsg('geographies')}
              />
            </TableCell>
          </>
        )}
      />
      {SETTINGS.app.enable_timeslots && (
        <Grid container spacing={3}>
          {props.rules[0].timeslots.map((timeslot, index) => {
            return (
              <Grid item xs={3} key={index}>
                <Grid container direction="column" spacing={1} style={{ marginTop: '13px' }}>
                  <Grid item component="h3">
                    {t('rule.time_slot_count', { index: index + 1 })}
                  </Grid>
                  <Grid item>
                    <TimeSlotForm
                      timeslot={timeslot}
                      handleChangeDays={handleChangeTimeslot(timeslot, index, 'days')}
                      handleChangeStartTime={handleChangeTimeslot(timeslot, index, 'start_time')}
                      handleChangeEndTime={handleChangeTimeslot(timeslot, index, 'end_time')}
                      handleRemoveTimeSlot={() => props.handleRemoveTimeSlot(index)}
                      disabled={props.isPublished && props.disabled}
                      error={isEndTimeBeforeStartTime(timeslot)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )
          })}
        </Grid>
      )}
    </>
  )
}

export default FleetSizeRule
